import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Empty, Input, Table, Tooltip } from "antd";
import { UiButton } from "../../ui";
import CgIcon from "./CgIcon";
import { UserContext } from "../../../context/UserContext";
import { debounce } from "../../../helpers/utility";
import { boardTint } from "../Dashboard/heroTheme";
import { useFittedRowsHeight } from "./useFittedRowsHeight";
import styles from "./listScreen.module.css";

/**
 * The chrome every pharmacy list screen wears, from the approved BYS mockup:
 * a head row with the screen's own title and actions, an optional distribution
 * band, and a card carrying a filter strip and the table.
 *
 * Configurations and Users deliberately do not use this - they are settings
 * screens rather than working lists.
 */

/**
 * The organisation's colour as an "r,g,b" triplet, for a screen that sets up its
 * own container and only needs the variable the stylesheet reads.
 */
export const useListTint = () => {
  const { userDetails } = useContext(UserContext);
  return boardTint(userDetails?.activeOrg?.color);
};

/** Wraps a screen and carries the organisation's colour down to it. */
export const ListScreen = ({ children }) => {
  const { userDetails } = useContext(UserContext);
  return (
    <div
      className={styles.wrap}
      style={{ "--o-tint": boardTint(userDetails?.activeOrg?.color) }}
    >
      {children}
    </div>
  );
};

/** Title on the left, the screen's actions on the right. */
export const ListHead = ({ title, actions }) => (
  <div className={styles.head}>
    <h2 className={styles.headTitle}>{title}</h2>
    {actions ? <div className={styles.headActions}>{actions}</div> : null}
  </div>
);

/**
 * The head row a record's own page leads with: a way back, the record's title,
 * whatever identifies it, and the actions that belong to it.
 */
export const DetailHead = ({ backLabel, onBack, title, meta, actions }) => (
  <div className={styles.detailHead}>
    {/*
      * The way back is its own line above the record. Sharing a line with the
      * number pushed the two together and read as one heading; on its own it
      * reads as what it is, and the number below it leads the page.
      */}
    {onBack ? (
      <div className={styles.detailBackRow}>
        <button type="button" className={styles.backLink} onClick={onBack}>
          <CgIcon name="chev" size={15} stroke={2.4} style={{ transform: "rotate(90deg)" }} />
          {backLabel || "Back"}
        </button>
      </div>
    ) : null}
    <div className={styles.detailTitleRow}>
      <h2 className={styles.headTitle}>{title}</h2>
      {meta}
      {actions ? <div className={styles.detailActions}>{actions}</div> : null}
    </div>
  </div>
);

/** A titled card on a detail page - the mockup's Invoice Header / Items. */
export const DetailCard = ({ title, count, extra, foot, children }) => (
  <section className={styles.card}>
    <div className={styles.cardHead}>
      <h3 className={styles.cardTitle}>{title}</h3>
      {count != null ? <span className={styles.cardCount}>{count}</span> : null}
      {extra ? <div className={styles.fbarRight}>{extra}</div> : null}
    </div>
    {children}
    {foot}
  </section>
);

/**
 * A detail card's own padding.
 *
 * The mockup's cards hold a grid of fields or a table, and a table runs to the
 * card's edge while fields do not - so the padding belongs to whatever is
 * inside rather than to the card.
 */
export const DetailBody = ({ children }) => (
  <div className={styles.detailBody}>{children}</div>
);

/**
 * A detail screen's cards: the record and its lines across the full width,
 * the summaries that close it side by side underneath.
 */
export const DetailGrid = ({ children }) => (
  <div className={styles.detailGrid}>{children}</div>
);

export const DetailFull = ({ children }) => (
  <div className={styles.detailFull}>{children}</div>
);

/**
 * The figures a record closes on: each under its own label, and the total
 * ruled off below them.
 *
 * `rows` are `{ label, value }`, or `{ label, control }` for one that is
 * typed rather than read.
 */
export const BillTotals = ({ rows = [], totalLabel, totalValue }) => (
  <>
    <div className={styles.billGrid}>
      {rows.map((row) => (
        <div key={row.label}>
          <span>{row.label}</span>
          {row.control ? row.control : <b>{row.value}</b>}
        </div>
      ))}
    </div>
    <div className={styles.billGrand}>
      <span>{totalLabel}</span>
      <b>{totalValue}</b>
    </div>
  </>
);

/**
 * What a card says in place of its table when the record has no lines yet.
 *
 * Purchase entries, orders and returns all open empty and all offer the same
 * first move, so they say it in the same words and the same place - the way in
 * sits under the explanation rather than off at the card's edge.
 */
export const DetailEmpty = ({ title, hint, children }) => (
  <Empty
    image={Empty.PRESENTED_IMAGE_SIMPLE}
    description={
      <div>
        <div className={styles.emptyTitle}>{title}</div>
        {hint ? <div className={styles.emptyHint}>{hint}</div> : null}
      </div>
    }
  >
    {children}
  </Empty>
);

/** A card's closing aside - what the screen wants understood, not figured. */
export const CardNote = ({ children }) => (
  <p className={styles.cardNote}>{children}</p>
);

/**
 * The batch a line is being bought or sold on, and what it expires on - one
 * control, the date under the number.
 */
export const BatchButton = ({ batch, expiry, disabled, onClick }) => (
  <div className={styles.batchCell}>
    {/*
      * The number reads as the control it is - a box with a chevron, like the
      * selects beside it in the row - and the expiry sits under it as a note
      * on what was picked rather than as a second line inside the box.
      */}
    <button
      type="button"
      className={`${styles.batchBtn} ${batch ? "" : styles.batchNone}`.trim()}
      title={batch || undefined}
      disabled={disabled}
      onClick={onClick}
    >
      <span className={styles.batchNo}>{batch || "+ Add Batch"}</span>
      <CgIcon name="chev" size={13} stroke={2.2} className={styles.batchChev} />
    </button>
    {batch && expiry ? <span className={styles.batchExp}>exp {expiry}</span> : null}
  </div>
);

/** One more line, offered as an outline rather than a solid button. */
export const AddLineButton = ({ onClick, disabled, children }) => (
  <button
    type="button"
    className={styles.addLine}
    disabled={disabled}
    onClick={onClick}
  >
    <CgIcon name="plus" size={15} stroke={2.2} />
    {children}
  </button>
);

/** The figure a detail card closes on, with anything else on its left. */
export const FootTotal = ({ label, value, children }) => (
  <div className={styles.footTotal}>
    {children}
    <div className={styles.footTotalRight}>
      <span className={styles.footTotalLabel}>{label}</span>
      <span className={styles.footTotalValue}>{value}</span>
    </div>
  </div>
);

/**
 * The band that leads a list: the figure, a segmented bar, the legend as
 * filters, and an optional figure at the end.
 *
 * `segments` are `{ key, label, count, color }`. Passing `activeKey` and
 * `onSelect` makes the bar and legend filter the list; leaving them out renders
 * the same band read-only.
 */
export const DistBand = ({
  total,
  label = "Records",
  segments = [],
  activeKey = null,
  onSelect,
  meta,
  aside,
}) => {
  const barTotal = total || 1;
  const clickable = Boolean(onSelect);

  return (
    <section className={styles.dist}>
      <div className={styles.distL}>
        <span className={styles.distV}>{total}</span>
        <span className={styles.distLab}>{label}</span>
      </div>

      <div className={styles.distR}>
        <div className={styles.distBar}>
          {segments
            .filter((s) => s.count)
            .map((s) => {
              const on = activeKey === s.key;
              return (
                <button
                  key={s.key}
                  type="button"
                  disabled={!clickable}
                  title={`${s.label} · ${s.count}`}
                  aria-label={`Show ${s.label}`}
                  className={`${styles.distSeg} ${on ? styles.on : ""} ${
                    activeKey && !on ? styles.dim : ""
                  }`}
                  style={{
                    width: `${(s.count / barTotal) * 100}%`,
                    background: s.color,
                    cursor: clickable ? "pointer" : "default",
                  }}
                  onClick={() => onSelect?.(s.key)}
                />
              );
            })}
        </div>

        <div className={styles.distKeys}>
          {segments.map((s) => (
            <button
              key={s.key}
              type="button"
              disabled={!clickable}
              className={`${styles.distKey} ${
                activeKey === s.key ? styles.on : ""
              }`}
              style={{ cursor: clickable ? "pointer" : "default" }}
              onClick={() => onSelect?.(s.key)}
            >
              <i style={{ background: s.color }} />
              {s.label}
              <b>{s.count ?? 0}</b>
            </button>
          ))}
          {meta ? <span className={styles.distMeta}>{meta}</span> : null}
        </div>
      </div>

      {aside ? (
        <div className={styles.distAside}>
          <span className={styles.distAsideL}>{aside.label}</span>
          <b>{aside.value}</b>
        </div>
      ) : null}
    </section>
  );
};

/**
 * The list itself: a titled card with a count, a filter strip, and whatever
 * table is passed as children. Paging is the table's own numbered pagination,
 * so the strip carries no "showing x of y" note. `onClear` adds the Clear
 * button.
 */
/*
 * Typing is instant but the list API is not asked on every keystroke: the box
 * keeps its own value and reports upward once the typing settles. `onChange`
 * is read through a ref because every caller passes a fresh inline arrow, and
 * a new function each render would rebuild - and so defeat - the debounce.
 */
const SEARCH_DEBOUNCE_MS = 300;

const ListSearchField = ({ search }) => {
  const [typed, setTyped] = useState(search.value ?? "");
  const onChangeRef = useRef(search.onChange);

  useEffect(() => {
    onChangeRef.current = search.onChange;
  }, [search.onChange]);

  // Keeps the box honest when the parent resets the term, as Clear does.
  useEffect(() => {
    setTyped(search.value ?? "");
  }, [search.value]);

  const notify = useMemo(
    () => debounce((e) => onChangeRef.current?.(e), SEARCH_DEBOUNCE_MS),
    []
  );

  return (
    <Input
      allowClear
      value={typed}
      onChange={(e) => {
        setTyped(e?.target?.value ?? "");
        notify(e);
      }}
      prefix={<CgIcon name="search" size={15} stroke={2} />}
      placeholder={search.placeholder || "Search"}
    />
  );
};

/**
 * Which rows of which set you are looking at, in the filter bar.
 *
 * The pager below says which page; this says what that page actually is, which
 * is the question a list of a few hundred rows raises. `overall` is the count
 * before filtering, shown only when a filter is narrowing things - otherwise it
 * would repeat the number beside it.
 */
export const ListRange = ({ page = 1, pageSize = PHARMACY_PAGE_SIZE, total = 0, overall }) => {
  if (!total) return <span className={styles.fshow}>No matches</span>;

  const size = pageSize || PHARMACY_PAGE_SIZE;
  const from = (page - 1) * size + 1;
  const to = Math.min(page * size, total);

  return (
    <span className={`${styles.fshow} ${styles.num}`}>
      {`Showing ${from}\u2013${to} of ${total}`}
      {overall && overall !== total ? ` \u00b7 ${overall} in all` : ""}
    </span>
  );
};

export const ListCard = ({ title, count, search, onClear, toolbar, range, children }) => (
  <section className={styles.card}>
    <div className={styles.cardHead}>
      <h3 className={styles.cardTitle}>{title}</h3>
      {count != null ? <span className={styles.cardCount}>{count}</span> : null}
    </div>

    {(search || onClear || toolbar || range) && (
      <div className={styles.fbar}>
        {search ? (
          <div className={styles.fbarSearch}>
            <ListSearchField search={search} />
          </div>
        ) : null}

        {toolbar}

        <div className={styles.fbarRight}>
          {onClear ? (
            <UiButton variant="secondary" size="sm" onClick={onClear}>
              Clear
            </UiButton>
          ) : null}
          {range ? <ListRange {...range} /> : null}
        </div>
      </div>
    )}

    {children}
  </section>
);


export const IconAction = ({ icon, label, onClick, disabled, tone }) => (
  <Tooltip title={label}>
    <button
      type="button"
      className={`${styles.iact} ${tone === "danger" ? styles.danger : ""}`}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
    >
      <CgIcon name={icon} size={16} stroke={2} />
    </button>
  </Tooltip>
);

export const RowActions = ({ children }) => (
  <div className={styles.rowActs}>{children}</div>
);

/* Cell helpers, so every table in the module renders the same shapes. */

/**
 * The record's own number - order, bill, entry.
 *
 * `dim` is for a reference to some other record, which the row mentions rather
 * than is: the bill a return came off, the order a bill was raised from.
 */
export const RefCell = ({ children, dim }) => (
  <span className={`${styles.orderNo} ${dim ? styles.refDim : ""}`.trim()}>
    {children || "-"}
  </span>
);

/**
 * A name, optionally followed by a small neutral tag such as an age, and an
 * optional quieter line under it for whatever identifies the person - an MRN,
 * a mobile number.
 */
export const NameCell = ({ name, tag, sub }) => {
  const line = (
    <div className={styles.who}>
      <span className={styles.pname} title={name}>
        {name || "-"}
      </span>
      {tag ? <span className={styles.agt}>{tag}</span> : null}
    </div>
  );

  if (!sub) return line;

  return (
    <div className={styles.stacked}>
      {line}
      <span className={styles.stackedSub} title={sub}>
        {sub}
      </span>
    </div>
  );
};

/** Cells that carry a tag or two side by side. */
export const CellRow = ({ children }) => (
  <div className={styles.who}>{children}</div>
);

/** How the sale is billed - IP, OP, a counter sale. */
export const TypeTag = ({ type, children }) => {
  const kind = type === "IP" ? "ip" : type === "COUNTER" ? "ctr" : "op";
  return (
    <span className={`${styles.admTag} ${styles[kind]}`}>
      {children ?? type}
    </span>
  );
};

/** Where the patient is, in the ward's own colour. */
export const BedTag = ({ children }) => (
  <span className={`${styles.agt} ${styles.bedt}`}>
    <CgIcon name="bed" size={12} stroke={2} />
    {children}
  </span>
);

export const Tag = ({ children }) => (
  <span className={styles.agt}>{children}</span>
);

/** Status pill. `tone` is [background, text]. */
export const StatusPill = ({ tone, children }) => (
  <span
    className={styles.pill}
    style={{ background: tone?.[0], color: tone?.[1] }}
  >
    {children}
  </span>
);

/** A name with a quiet second line - salt, type, HSN. */
export const StackedCell = ({ name, sub }) => (
  <div className={styles.stacked}>
    <span className={styles.pname} title={name}>
      {name || "-"}
    </span>
    {sub ? (
      <span className={styles.stackedSub} title={sub}>
        {sub}
      </span>
    ) : null}
  </div>
);

/** A figure that colours itself. `tone` is [background, text]. */
export const FigurePill = ({ tone, children }) => (
  <span
    className={styles.stockPill}
    style={{ background: tone?.[0], color: tone?.[1] }}
  >
    {children}
  </span>
);

export const DimCell = ({ children }) => (
  <span className={styles.dim}>{children}</span>
);

export const NumCell = ({ children }) => (
  <span className={styles.num}>{children}</span>
);

/**
 * How many rows a pharmacy list asks for at a time.
 *
 * Sent as `limit` on every list request, so the server pages to the same size
 * the table is drawn for. One constant rather than a number per screen, because
 * a list that pages differently from the one beside it is a list that looks
 * broken.
 */
export const PHARMACY_PAGE_SIZE = 25;

/**
 * The page size the server is actually using, read off its own response.
 *
 * The lists return `total_pages`, so when there is more than one page the rows
 * that came back are a full page and therefore the page size itself. Guessing a
 * size instead would mislabel the page numbers as soon as the server's differed.
 */
export const pageSizeOf = (data, fallback = PHARMACY_PAGE_SIZE) => {
  const rows = data?.results?.length || 0;
  const count = Number(data?.count) || 0;
  const pages = Number(data?.total_pages) || 0;
  const current = Number(data?.current_page) || 0;

  /*
   * How many rows a full page holds, which is what the pager divides the total
   * by to decide how many pages there are.
   *
   * The rows in hand only answer that on a page that is actually full. This
   * read them on any page, so the last one - 24 rows of 99, in 4 pages of 25 -
   * had the pager dividing by 24 and offering a fifth page that does not
   * exist. A short last page is answered from the server's own two figures
   * instead.
   */
  if (pages > 1 && rows && current && current < pages) return rows;
  if (pages > 1 && count) return Math.ceil(count / pages);

  return count || rows || fallback;
};

/** Apply to an antd <Table> so it wears the module's header and row styling. */
export const listTableClass = styles.table;

/**
 * A list screen's table: the module's styling, every column it has, and the
 * rows scrolling inside the card rather than taking the page with them.
 *
 * The height is measured from where the rows actually start and what actually
 * sits under them, so the card ends up the height of the window whatever the
 * screen is carrying above it - a filter strip, a distribution band, a row of
 * tabs. The head and the pager stay put; only the rows move.
 *
 * `fitDeps` are anything else that changes what surrounds the rows. The rows
 * themselves, the spinner and the pager are watched already.
 */
export const ListTable = ({ scroll, fitDeps = [], ...props }) => {
  const rowsRef = useRef(null);
  const rowsHeight = useFittedRowsHeight(rowsRef, [
    props.dataSource,
    props.loading,
    props.pagination?.current,
    props.pagination?.total,
    ...fitDeps,
  ]);

  return (
    <div ref={rowsRef}>
      <Table
        className={styles.table}
        scroll={{ x: "max-content", y: rowsHeight, ...scroll }}
        {...props}
      />
    </div>
  );
};

export default ListScreen;
